import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'config/constants/number'
import { InhouseGameDetails } from 'config/types/game'
import Column from 'layout/Components/Column'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useValidatePlayToken } from 'state/app/hooks'
import { useTokenSelected } from 'state/session/hooks'
import { BoxProps } from 'UIKit/Box/types'
import TransText from 'UIKit/Text/TransText'
import BetBoardTabs from 'views/InhouseGame/components/BetBoardTabs'
import BetAmount from 'views/InhouseGame/components/Controls/BetAmount'
import { StyledWrapper } from 'views/InhouseGame/components/styled'
import { GameInHouseConfig, InhouseGameBetBoardTab } from 'views/InhouseGame/config/types'
import { InhouseGameSettings } from '../../config/types'
import { useCurrentMineBet, useMineAction, useMineGameStatus } from '../../hooks'
import MineAutoBet from './MineAutoBet'
import MineManualBet from './MineManualBet'
import MinePlay from './MinePlay'

const MineBetSettings: React.FC<
  GameInHouseConfig &
    Omit<BoxProps, 'onVolumeChange'> & {
      disabled?: boolean
      isLocked?: boolean
      setIsLocked: (value: boolean) => void
      tab: InhouseGameBetBoardTab
      setTab: (value: InhouseGameBetBoardTab) => void
      mines: number
      game: InhouseGameDetails
      gameSettings: InhouseGameSettings
      setMines: (mines: number) => void
      selectedBlocks?: number[]
    }
> = ({
  volumeEnable,
  instanceBetEnable,
  maxBetButtonEnable,
  hotkeyEnable,
  disabled,
  setIsLocked,
  isLocked,
  tab,
  mines,
  setMines,
  setTab,
  selectedBlocks,
  gameSettings,
  game,
  ...props
}) => {
  const { isPlaying } = useMineGameStatus()
  const currentBet = useCurrentMineBet()

  const playToken = useTokenSelected()
  const { isBlackList } = useValidatePlayToken(playToken)
  const { submitBet } = useMineAction()
  const updateBetAmountRef = useRef(null)

  const [betTokenAmount, setBetTokenAmount] = useState(null)
  const [isAmountInvalid, setIsAmountInvalid] = useState(false)

  const isDisabled = isAmountInvalid || (isBlackList && !currentBet) || disabled || game.isMaintenance

  useEffect(() => {
    if (currentBet && !currentBet.betAmount.amount.eq(betTokenAmount?.amount)) {
      setBetTokenAmount(currentBet.betAmount)
      updateBetAmountRef.current(currentBet.betAmount.amount.toString())
    }
  }, [currentBet])

  const handleBet = useCallback(
    async (amount?: BigNumber) => {
      if (disabled) return

      const parsedAmount = amount?.isNaN() ? BIG_ZERO : amount
      if (parsedAmount && updateBetAmountRef.current)
        updateBetAmountRef.current(Number(parsedAmount.toFixed(2)).toString())

      const betAmount = parsedAmount
        ? new BigNumber(parsedAmount.toFixed(2))
        : betTokenAmount.amount?.isNaN()
        ? BIG_ZERO
        : betTokenAmount.amount

      return submitBet({ amount: betAmount, token: betTokenAmount.token }, mines, {
        instanceBetEnable,
        volumeEnable,
      })
    },
    [submitBet, disabled, betTokenAmount, volumeEnable, instanceBetEnable, mines],
  )

  const betAmountInitAuto = useRef<BigNumber>(null)
  const onAutoBetStart = useCallback(() => {
    betAmountInitAuto.current = betTokenAmount?.amount

    setIsLocked(true)
  }, [setIsLocked, betTokenAmount])

  const onAutoBetFinish = useCallback(() => {
    const parsedBetAmountInitAuto =
      betAmountInitAuto.current &&
      !betAmountInitAuto.current.isNaN() &&
      Number(betAmountInitAuto.current.toFixed(2)).toString()
    if (!parsedBetAmountInitAuto) {
      setIsLocked(false)
      return
    }
    updateBetAmountRef.current(parsedBetAmountInitAuto)
    betAmountInitAuto.current = null
    setIsLocked(false)
  }, [setIsLocked, betTokenAmount])

  const parsedIsPlaying = isPlaying || (currentBet && !currentBet.isCompleted)
  return (
    <StyledWrapper {...props} minHeight={['unset', '', '', '', '600px']} pb={['16px !important']}>
      <BetBoardTabs tab={tab} setTab={setTab} disabled={isPlaying || isLocked || game.isMaintenance} />

      <Column>
        <BetAmount
          inputId="plinko-bet-amount-input"
          setBetTokenAmount={setBetTokenAmount}
          setIsAmountInvalid={setIsAmountInvalid}
          disabled={parsedIsPlaying || isLocked || game.isMaintenance}
          mb="14px"
          height="40px"
          placeholder="0"
          maxButton={maxBetButtonEnable}
          hotKey={hotkeyEnable}
          isBlackListToken={!currentBet && isBlackList}
          updateBetAmountRef={updateBetAmountRef}
          hideEstToToken={parsedIsPlaying && tab !== InhouseGameBetBoardTab.Auto}
          maxBet={gameSettings?.maxBetAmountUsd}
        />
        {tab === InhouseGameBetBoardTab.Manual &&
          (parsedIsPlaying ? (
            <MinePlay hotkeyEnable={hotkeyEnable} mines={mines} />
          ) : (
            <MineManualBet
              betAmount={betTokenAmount}
              setMines={setMines}
              disabled={isDisabled}
              hotkeyEnable={hotkeyEnable}
              mines={mines}
              onBet={handleBet}
            />
          ))}

        {tab === InhouseGameBetBoardTab.Auto && (
          <MineAutoBet
            setMines={setMines}
            mines={mines}
            onAutoBetStart={onAutoBetStart}
            onAutoBetEnd={onAutoBetFinish}
            tokenAmount={betTokenAmount}
            disabled={isDisabled}
            hotkeyEnable={hotkeyEnable}
            onBet={handleBet}
            selectedBlocks={selectedBlocks}
            maxBet={gameSettings?.maxBetAmountUsd}
          />
        )}
      </Column>
      {game.isMaintenance && (
        <TransText small textAlign="center" mt="8px" lineHeight="1.2">
          This game is under maintenance
        </TransText>
      )}
    </StyledWrapper>
  )
}

export default MineBetSettings
